class BoundEvent
{
	target: EventTarget;
	type: string;
	callback: () => void;
}

export abstract class Component
{
	private localEvents: { [index: string]: Array<Function> } = {};
	private boundEvents: Array<BoundEvent> = [];
	
	addLocalEventListener(type: string, callback: Function): void
	{
		if (!this.localEvents[type])
		{
			this.localEvents[type] = [];
		}
		
		if (this.localEvents[type].indexOf(callback) == -1)
		{
			this.localEvents[type].push(callback);
		}
	}

	removeLocalEventListener(type: string, callback: Function): void
	{
		if (this.localEvents[type])
		{
			let index = this.localEvents[type].indexOf(callback);

			if (index > -1)
			{
				this.localEvents[type].splice(index, 1);
				
				if (this.localEvents[type].length == 0)
				{
					delete this.localEvents[type];
				}
			}
		}
	}

	dispatchLocalEvent(type: string, ...args: any[]): Promise<void>
	{
		return new Promise(function(this: Component, resolve: Function, reject: (reason: any) => void)
		{
			let loop = function(this: Component, i: number)
			{
				if (this.localEvents[type] && this.localEvents[type].length > i)
				{
					let ret = this.localEvents[type][i].apply(this, args);
					
					if (ret instanceof Promise)
					{
						ret.then(() => { loop.call(this, i + 1); }, reject);
					}
					else
					{
						loop.call(this, i + 1);
					}
				}
				else
				{
					resolve();
				}
			}.bind(this);

			loop(0);
		}.bind(this));
	}
	
	protected addBoundEventListener(elem: EventTarget, type: string, callback: Function)
	{
		let cb = callback.bind(this);

		elem.addEventListener(type, cb);

		this.boundEvents.push({
			'target': elem,
			'type': type,
			'callback': cb
		});
	}
	
	protected removeBoundEventListeners()
	{
		let e;

		for (let i = this.boundEvents.length - 1; i >= 0; i--)
		{
			e = this.boundEvents[i];
			e.target.removeEventListener(e.type, e.callback);

			this.boundEvents.splice(i, 1);
		}
	}

	destroy(): void
	{
		this.removeBoundEventListeners();
	}
}

class Registration
{
	private readonly componentClass: new(element: HTMLElement) => Component;
	private readonly tagNames: Array<string>;
	private readonly className: string;
	private readonly query: string;
	private observer?: MutationObserver;

	constructor(componentClass: new(element: HTMLElement) => Component, tagNames: Array<string>, className: string, query: string)
	{
		this.componentClass = componentClass;
		this.tagNames = tagNames;
		this.className = className;
		this.query = query;
	}

	init()
	{
		let elements = document.querySelectorAll(this.query);

		for (let i = 0; i < elements.length; i++)
		{
			this.attach(elements[i]);
		}

		if (this.observer)
		{
			this.observer.disconnect();
		}

		let thisObj = this;

		this.observer = new MutationObserver(function (mutationsList)
		{
			for (let i = 0; i < mutationsList.length; i++)
			{
				if (mutationsList[i].type == 'childList')
				{
					mutationsList[i].addedNodes.forEach(function (value)
					{
						thisObj.attach(value);

						if (value instanceof HTMLElement)
						{
							let elements = value.querySelectorAll(thisObj.query);

							for (let i = 0; i < elements.length; i++)
							{
								thisObj.attach(elements[i]);
							}
						}
					});

					mutationsList[i].removedNodes.forEach(function (value)
					{
						if (value instanceof HTMLElement)
						{
							let elements = value.querySelectorAll(thisObj.query);

							for (let i = 0; i < elements.length; i++)
							{
								thisObj.detach(elements[i]);
							}
						}

						thisObj.detach(value);
					});
				}
			}
		});

		this.observer.observe(document, {childList: true, subtree: true});
	}

	reload()
	{
		let elements = document.querySelectorAll(this.query);

		for (let i = 0; i < elements.length; i++)
		{
			this.detach(elements[i]);
			this.attach(elements[i]);
		}
	}

	isValidElement(element: Node)
	{
		if (element instanceof HTMLElement)
		{
			return this.tagNames.indexOf(element.tagName.toLowerCase()) > -1 && element.classList.contains(this.className);
		}
		else
		{
			return false;
		}
	}

	attach(element: Node)
	{
		if (this.isValidElement(element) && !(element as any).componentObject)
		{
			(element as any).componentObject = new this.componentClass(element as HTMLElement);
		}
	}

	detach(element: Node)
	{
		if (this.isValidElement(element) && (element as any).componentObject)
		{
			let component = (element as any).componentObject as Component;

			component.destroy();

			(element as any).componentObject = null;
		}
	}
}

class ComponentRegistryService
{
	private initialized: boolean;
	private registrations: Array<Registration>;

	constructor()
	{
		this.initialized = false;
		this.registrations = [];
	}

	register(componentClass: new(element: HTMLElement) => Component, tagName: string | Array<string>, className: string): void
	{
		let tagNames;

		if (tagName instanceof Array)
		{
			tagNames = tagName;
		}
		else
		{
			tagNames = [tagName as string];
		}

		let query = '';

		for (let i = 0; i < tagNames.length; i++)
		{
			if (query != '')
			{
				query += ',';
			}

			query = tagNames[i] + '.' + className;
		}

		let registration = new Registration(componentClass, tagNames, className, query);

		this.registrations.push(registration);

		if (this.initialized)
		{
			registration.init();
		}
	}

	init()
	{
		if (!this.initialized)
		{
			this.initialized = true;

			for (let i = 0; i < this.registrations.length; i++)
			{
				this.registrations[i].init();
			}
		}
	}

	reload()
	{
		for (let i = 0; i < this.registrations.length; i++)
		{
			this.registrations[i].reload();
		}
	}

	getComponent(element: HTMLElement)
	{
		return (element as any).componentObject;
	}
}

export const ComponentRegistry = new ComponentRegistryService();
export default { ComponentRegistry, Component };
